<template>
  <v-container fluid class="pa-0">
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title>
        {{ $t("moduli_rr.overview") }}
      </v-app-bar-title>
    </v-app-bar>

    <div class="pa-3">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="#a6160b"
        class="mb-3"
      />

      <v-row class="mt-0">
        <v-col
          v-for="card in summaryCards"
          :key="card.key"
          cols="6"
          sm="6"
          md="4"
          lg
          class="pa-1"
        >
          <v-card
            :color="card.color"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <div class="d-flex align-center ga-3">
                <v-icon
                  :size="$vuetify.display.xs ? 22 : 50"
                  color="white"
                >
                  {{ card.icon }}
                </v-icon>
                <div class="text-h4 font-weight-bold text-white">
                  <template v-if="!loading">
                    {{ formatCount(card.value) }}
                  </template>
                  <v-progress-circular
                    v-else
                    indeterminate
                    size="28"
                    width="3"
                    color="white"
                  />
                </div>
              </div>
              <div class="text-subtitle-1 text-white mt-2">
                {{ card.label }}
              </div>
              <div
                v-if="card.detail"
                class="text-caption text-white mt-1 d-none d-sm-block"
              >
                {{ card.detail }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-2" align="stretch">
        <v-col cols="12" lg="7" class="pa-1">
          <v-card flat border class="pa-4 fill-height">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              {{ $t("moduli_rr.overview_stats.parts_by_institution") }}
            </div>
            <div class="text-caption text-medium-emphasis mb-4">
              {{ $t("moduli_rr.overview_stats.parts_by_institution_hint") }}
            </div>
            <chart
              v-if="!loading && partsChartData.labels.length"
              :key="partsChartKey"
              type="horizontalBar"
              :data="partsChartData"
              :height="partsChartHeight"
              :extra-options="partsChartOptions"
              :plugins="partsChartPlugins"
            />
            <v-alert
              v-else-if="!loading"
              type="info"
              variant="tonal"
              density="compact"
            >
              {{ $t("moduli_rr.overview_stats.no_chart_data") }}
            </v-alert>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5" class="pa-1">
          <div class="d-flex flex-column ga-3 fill-height">
            <!-- Concentration -->
            <v-card flat border class="pa-4">
              <div class="text-body-2 text-medium-emphasis mb-2">
                {{ $t("moduli_rr.overview_stats.inventory_concentration") }}
              </div>
              <div class="d-flex align-baseline ga-2 flex-wrap">
                <span class="text-h4 font-weight-bold">
                  {{ partsInsights.concentrationPct }}%
                </span>
                <span class="text-body-2 text-medium-emphasis">
                  {{ $t("moduli_rr.overview_stats.in_top_5") }}
                </span>
              </div>
              <div class="rr-concentration-bar mt-4">
                <div
                  v-for="(seg, i) in partsInsights.topSegments"
                  :key="`seg-${i}`"
                  class="rr-concentration-bar__seg"
                  :style="{
                    width: `${seg.pct}%`,
                    backgroundColor: TOP_SEGMENT_COLORS[i],
                  }"
                  :title="`${seg.title}: ${formatCount(seg.count)} (${seg.pct}%)`"
                />
                <div
                  v-if="partsInsights.remainderPct > 0"
                  class="rr-concentration-bar__seg rr-concentration-bar__seg--rest"
                  :style="{ width: `${partsInsights.remainderPct}%` }"
                />
              </div>
            </v-card>

            <!-- Top 5 -->
            <v-card flat border class="pa-4">
              <div class="text-body-2 text-medium-emphasis mb-3">
                {{ $t("moduli_rr.overview_stats.parts_top_5") }}
              </div>
              <div
                v-for="(row, i) in partsInsights.top5"
                :key="row.institution_id || row.title"
                class="d-flex align-center justify-space-between py-2"
                :class="{ 'rr-rank-row': i < partsInsights.top5.length - 1 }"
              >
                <span class="text-body-2">{{ row.title }}</span>
                <span class="text-body-2 font-weight-bold text-no-wrap">
                  {{ formatCount(row.count) }}
                  <span class="text-medium-emphasis font-weight-regular">
                    ({{ row.pct }}%)
                  </span>
                </span>
              </div>
              <div
                v-if="!partsInsights.top5.length && !loading"
                class="text-caption text-medium-emphasis"
              >
                {{ $t("moduli_rr.overview_stats.no_chart_data") }}
              </div>
            </v-card>

            <!-- Avg / Median -->
            <v-row dense>
              <v-col cols="6">
                <v-card flat border class="pa-4 ">
                  <div class="text-body-2 text-medium-emphasis mb-2">
                    {{ $t("moduli_rr.overview_stats.avg_per_institution") }}
                  </div>
                  <div class="text-h5 font-weight-bold">
                    {{ formatCount(partsInsights.average) }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="6">
                <v-card flat border class="pa-4 ">
                  <div class="text-body-2 text-medium-emphasis mb-2">
                    {{ $t("moduli_rr.overview_stats.median") }}
                  </div>
                  <div class="text-h5 font-weight-bold">
                    {{ formatCount(partsInsights.median) }}
                  </div>
                </v-card>
              </v-col>
                  
            </v-row>

        
          </div>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12" class="pa-1">
          <activity-chart :institutions="stats.institutions" />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import getRequest from "@/api/getRequest";
import Chart from "@/components/chart.vue";
import ActivityChart from "@/components/ModuliRr/Overview/activityChart.vue";

const CHART_BAR_MUTED = "#85B7EB";
const CHART_BAR_TOP = "#185FA5";

const TOP_SEGMENT_COLORS = [
  "#185FA5",
  "#2B7BB8",
  "#4A95C9",
  "#6AADD8",
  "#85B7EB",
];

const LOW_PARTS_THRESHOLD = 3000;

/** Always group thousands with `.` (it-IT skips grouping under 10.000). */
function formatItInt(value) {
  const n = Math.round(Number(value) || 0);
  const sign = n < 0 ? "-" : "";
  return sign + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default {
  name: "ModuliRrOverview",
  components: {
    chart: Chart,
    "activity-chart": ActivityChart,
  },
  data: () => ({
    loading: false,
    TOP_SEGMENT_COLORS,
    LOW_PARTS_THRESHOLD,
    stats: {
      rr_institutions: 0,
      rr_members: 0,
      rr_members_available: false,
      rr_parts: 0,
      rr_orders: 0,
      rr_sales: 0,
      parts_by_institution: [],
      institutions: [],
    },
  }),
  computed: {
    summaryCards() {
      return [
        {
          key: "institutions",
          color: "#1F7087",
          icon: "mdi-domain",
          label: this.$t("moduli_rr.overview_stats.rr_institutions"),
          value: this.stats.rr_institutions,
          detail: this.$t("moduli_rr.overview_stats.rr_institutions_hint"),
        },
        {
          key: "parts",
          color: "#3f699e",
          icon: "mdi-package-variant-closed",
          label: this.$t("moduli_rr.overview_stats.rr_parts"),
          value: this.stats.rr_parts,
          detail: this.$t("moduli_rr.overview_stats.rr_parts_hint"),
        },
        {
          key: "orders",
          color: "#8bb28d",
          icon: "mdi-cart-outline",
          label: this.$t("moduli_rr.overview_stats.rr_orders"),
          value: this.stats.rr_orders,
          detail: this.$t("moduli_rr.overview_stats.rr_orders_hint"),
        },
        {
          key: "sales",
          color: "#5b98aa",
          icon: "mdi-cash-register",
          label: this.$t("moduli_rr.overview_stats.rr_sales"),
          value: this.stats.rr_sales,
          detail: this.$t("moduli_rr.overview_stats.rr_sales_hint"),
        },
      ];
    },
    rankedInstitutions() {
      return [...(this.stats.parts_by_institution || [])].sort(
        (a, b) => (b.count || 0) - (a.count || 0),
      );
    },
    partsInsights() {
      const rows = this.rankedInstitutions;
      const total =
        rows.reduce((sum, r) => sum + (Number(r.count) || 0), 0) || 0;
      const top5 = rows.slice(0, 5).map((r) => {
        const count = Number(r.count) || 0;
        const pct = total ? Math.round((count / total) * 1000) / 10 : 0;
        return {
          ...r,
          count,
          pct,
        };
      });
      const topSum = top5.reduce((sum, r) => sum + r.count, 0);
      const concentrationPct = total
        ? Math.round((topSum / total) * 100)
        : 0;
      const topSegments = top5.map((r) => ({
        title: r.title,
        count: r.count,
        pct: total ? Math.round((r.count / total) * 1000) / 10 : 0,
      }));
      const remainderPct = Math.max(
        0,
        Math.round((100 - topSegments.reduce((s, x) => s + x.pct, 0)) * 10) /
          10,
      );
      const n = rows.length;
      const average = n ? Math.round(total / n) : 0;
      const counts = rows.map((r) => Number(r.count) || 0);
      let median = 0;
      if (counts.length) {
        const mid = Math.floor(counts.length / 2);
        median =
          counts.length % 2 === 0
            ? Math.round((counts[mid - 1] + counts[mid]) / 2)
            : counts[mid];
      }
      const lowCount = rows.filter(
        (r) => (Number(r.count) || 0) < LOW_PARTS_THRESHOLD,
      ).length;

      return {
        top5,
        topSegments,
        concentrationPct,
        remainderPct,
        average,
        median,
        lowCount,
      };
    },
    partsChartData() {
      const rows = this.rankedInstitutions;
      return {
        labels: rows.map((r) => r.title),
        datasets: [
          {
            label: this.$t("moduli_rr.overview_stats.parts_dataset"),
            data: rows.map((r) => r.count),
            backgroundColor: rows.map((_, i) =>
              i === 0 ? CHART_BAR_TOP : CHART_BAR_MUTED,
            ),
            borderWidth: 0,
          },
        ],
      };
    },
    partsChartHeight() {
      const n = this.rankedInstitutions.length;
      return Math.max(320, n * 28 + 80);
    },
    partsChartKey() {
      return `rr-parts-${this.rankedInstitutions.length}-${this.stats.rr_parts}`;
    },
    partsChartOptions() {
      return {
        legend: { display: false },
        layout: {
          padding: { right: 56 },
        },
        scales: {
          xAxes: [
            {
              ticks: {
                beginAtZero: true,
                precision: 0,
                callback(value) {
                  return formatItInt(value);
                },
              },
              gridLines: { color: "rgba(0,0,0,0.06)" },
            },
          ],
          yAxes: [
            {
              gridLines: { display: false },
              ticks: { fontSize: 11 },
            },
          ],
        },
      };
    },
    partsChartPlugins() {
      return [
        {
          afterDatasetsDraw(chart) {
            const { ctx } = chart;
            const meta = chart.getDatasetMeta(0);
            if (!meta || !meta.data) return;
            ctx.save();
            ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
            ctx.font = "600 11px sans-serif";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            meta.data.forEach((bar, index) => {
              const value = chart.data.datasets[0].data[index];
              if (value == null) return;
              const model = bar._model || bar;
              const x = (model.x != null ? model.x : bar.tooltipPosition().x) + 8;
              const y = model.y != null ? model.y : bar.tooltipPosition().y;
              ctx.fillText(formatItInt(value), x, y);
            });
            ctx.restore();
          },
        },
      ];
    },
  },
  mounted() {
    this.loadOverview();
  },
  methods: {
    formatCount(value) {
      return formatItInt(value);
    },
    loadOverview() {
      this.loading = true;
      getRequest(
        "/moduli-rr/overview",
        `institution_id=${this.$store.getters.institutionId}&role=${this.$store.getters.role}`,
      )
        .then((response) => {
          this.stats = {
            rr_institutions: response.rr_institutions || 0,
            rr_members: response.rr_members || 0,
            rr_members_available: !!response.rr_members_available,
            rr_parts: response.rr_parts || 0,
            rr_orders: response.rr_orders || 0,
            rr_sales: response.rr_sales || 0,
            parts_by_institution: response.parts_by_institution || [],
            institutions: response.institutions || [],
          };
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.rr-concentration-bar {
  display: flex;
  width: 100%;
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: #e5e7eb;
}
.rr-concentration-bar__seg {
  height: 100%;
  min-width: 2px;
}
.rr-concentration-bar__seg--rest {
  background: #e5e7eb;
}
.rr-rank-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.rr-alert-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #8d6e63;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
</style>
